import { useEffect, useState } from 'react';

import styled from 'styled-components';

import { Collapse } from '@components/animators/collapse';
import { Rotate } from '@components/animators/Rotate';
import { Button } from '@components/button';
import { IconDropRight } from '@components/icons/dropright';
import { Text } from '@components/text';
import { useIsOverflowing } from '@hooks/use-is-overflowing';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { ReviewFinding, ReviewTargetKind } from 'api/journey-reviews/requests';

import { FindingTargetIcon } from './finding-target-icon';
import { severityColor } from './review-utils';
import { useHighlightedFindingStore } from './use-highlighted-finding-store';

import { JourneyNode } from '../node-types/types';

export type FindingCardProps = {
  finding: ReviewFinding;
  node?: JourneyNode;
  onJumpTo: (finding: ReviewFinding) => void;
  // Exactly one of these is provided: onIgnore for open findings, onRestore for
  // the ignored tab.
  onIgnore?: (finding: ReviewFinding) => void;
  onRestore?: (finding: ReviewFinding) => void;
};

const KIND_LABEL_KEY: Record<ReviewTargetKind, keyof (typeof locale)['en']> = {
  action: 'kindAction',
  trigger: 'kindTrigger',
  branch: 'kindBranch',
  field: 'kindField',
};

const OPEN_LABEL_KEY: Record<ReviewTargetKind, keyof (typeof locale)['en']> = {
  action: 'openAction',
  trigger: 'openTrigger',
  branch: 'openBranch',
  field: 'openField',
};

export const FindingCard = ({ finding, node, onJumpTo, onIgnore, onRestore }: FindingCardProps) => {
  const { t } = useScopedTranslate(locale);
  const [isExpanded, setIsExpanded] = useState(false);
  const [descriptionRef, isDescriptionClamped] = useIsOverflowing('vertical');
  const highlightFinding = useHighlightedFindingStore((state) => state.highlightFinding);
  const clearFinding = useHighlightedFindingStore((state) => state.clearFinding);

  // Ignoring the card while the pointer is on it would otherwise leave the
  // node outlined with nothing hovered.
  useEffect(() => () => clearFinding(finding.target), [clearFinding, finding.target]);

  // `isExpanded` first: once open the clamp is gone, so the measurement reports
  // "fits" and the control would disappear under the pointer.
  const canExpand = isExpanded || isDescriptionClamped || Boolean(finding.suggestedFix);

  const kind = t(KIND_LABEL_KEY[finding.target.kind]);
  const summary = (
    <>
      <FindingTargetIcon target={finding.target} node={node} />
      <SummaryText>
        <Text font="semibold14" text={finding.title} />
        <Text
          font="regular12"
          color="textHelper"
          text={finding.target.label ? `${kind} · ${finding.target.label}` : kind}
        />
        <Description ref={descriptionRef} $isClamped={!isExpanded}>
          <Text font="regular14" color="textSecondary" text={finding.description} />
        </Description>
      </SummaryText>
      {canExpand && (
        <Chevron isRotated={isExpanded}>
          <IconDropRight color="textHelper" />
        </Chevron>
      )}
    </>
  );

  return (
    <Container
      data-testid="finding-card"
      onMouseEnter={() => highlightFinding(finding.target, severityColor[finding.severity])}
      onMouseLeave={() => clearFinding(finding.target)}
    >
      {canExpand ? (
        <SummaryButton
          type="button"
          aria-expanded={isExpanded}
          onClick={() => setIsExpanded((expanded) => !expanded)}
        >
          {summary}
        </SummaryButton>
      ) : (
        <Summary>{summary}</Summary>
      )}

      <Collapse isCollapsed={!isExpanded || !finding.suggestedFix} overflowType="hidden">
        <SuggestedFix>
          <Text font="regular14">
            <FixLabel>{t('fixLabel')}</FixLabel> {finding.suggestedFix}
          </Text>
        </SuggestedFix>
      </Collapse>

      {/* Outside the summary, so acting on a finding never costs an expand first. */}
      <Actions>
        <Button
          variant="secondary"
          size="xs"
          text={t(OPEN_LABEL_KEY[finding.target.kind])}
          onClick={() => onJumpTo(finding)}
        />
        {onRestore ? (
          <Button variant="ghost" size="xs" text={t('reopen')} onClick={() => onRestore(finding)} />
        ) : onIgnore ? (
          <Button variant="ghost" size="xs" text={t('ignore')} onClick={() => onIgnore(finding)} />
        ) : null}
      </Actions>
    </Container>
  );
};

const Container = styled.div({
  padding: pxToRem(12),
  borderRadius: pxToRem(8),
  border: `1px solid ${theme.color.border10}`,
  background: theme.color.backgroundLightDark,
});

const summaryLayout = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: pxToRem(8),
  width: '100%',
  textAlign: 'left',
  padding: 0,
  border: 'none',
  background: 'none',
  color: 'inherit',
} as const;

const Summary = styled.div(summaryLayout);

const SummaryButton = styled.button({
  ...summaryLayout,
  cursor: 'pointer',
});

const SummaryText = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: pxToRem(2),
  flex: 1,
  minWidth: 0,
});

const Description = styled.div<{ $isClamped: boolean }>((props) => ({
  marginTop: pxToRem(2),
  ...(props.$isClamped && {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    overflow: 'hidden',
  }),
}));

const Chevron = styled(Rotate)({
  flexShrink: 0,
  marginTop: pxToRem(4),
});

const SuggestedFix = styled.div({
  marginTop: pxToRem(8),
  padding: `${pxToRem(8)} ${pxToRem(12)}`,
  borderRadius: pxToRem(4),
  background: theme.gradient.aiSurface10,
});

const FixLabel = styled.span({
  font: theme.font.semibold14,
});

const Actions = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  gap: pxToRem(4),
  marginTop: pxToRem(10),
  paddingTop: pxToRem(10),
  borderTop: `1px solid ${theme.color.border10}`,
});

export const locale = {
  en: {
    kindAction: 'Action',
    kindTrigger: 'Trigger',
    kindBranch: 'Branch',
    kindField: 'Field',
    openAction: 'Open action',
    openTrigger: 'Open trigger',
    openBranch: 'Open branch',
    openField: 'Open field',
    ignore: 'Ignore',
    reopen: 'Reopen',
    fixLabel: 'Fix:',
  },
};
